/**
 * Parts approval submission history.
 *
 * One row per Submit-for-Approval action, plus one row per direct Finalize
 * action by an Admin. Cycles of reject-revise-resubmit accumulate as multiple
 * rows. The current state of the workflow is derivable from the most recent
 * row's status combined with the project's `partsWorkflowState`.
 *
 * Constraint: at most one submission per project may have status PENDING
 * (enforced via partial unique index).
 */

import { Type, type Static } from '@sinclair/typebox';
import { SubmissionReviewActions, SubmissionStatuses } from '../enums.js';
import { FIELD_LIMITS } from '../constants.js';
import { IsoDateTimeSchema, RowVersionField, TimestampFields, UuidSchema } from './common.js';

export const PartsApprovalSubmissionSchema = Type.Object({
  id: UuidSchema,
  projectId: UuidSchema,
  submittedByUserId: UuidSchema,
  submittedAt: IsoDateTimeSchema,

  status: Type.Union(SubmissionStatuses.map((s) => Type.Literal(s))),

  reviewedByUserId: Type.Union([UuidSchema, Type.Null()]),
  reviewedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
  reviewAction: Type.Union([
    Type.Union(SubmissionReviewActions.map((a) => Type.Literal(a))),
    Type.Null(),
  ]),
  rejectionComment: Type.Union([
    Type.String({ maxLength: FIELD_LIMITS.REJECTION_COMMENT_MAX }),
    Type.Null(),
  ]),

  ...TimestampFields,
  ...RowVersionField,
});
export type PartsApprovalSubmission = Static<typeof PartsApprovalSubmissionSchema>;

// ---------------------------------------------------------------------------
// Action inputs (no body needed for Submit; project context comes from path)
// ---------------------------------------------------------------------------

export const PartsApprovalRejectSchema = Type.Object({
  rejectionComment: Type.Optional(
    Type.String({ minLength: 1, maxLength: FIELD_LIMITS.REJECTION_COMMENT_MAX }),
  ),
});
export type PartsApprovalReject = Static<typeof PartsApprovalRejectSchema>;
